import { useState } from "react";
import { Button, Dialog } from "@rio-cloud/rio-uikit";
import RadioButton from "@rio-cloud/rio-uikit/RadioButton";
import { Occurrence } from "../../../models/occurrence";
import SuccessDialog from "../../dialogs/SuccessDialog";
import ErrorDialog from "../../dialogs/ErrorDialog";

interface IFinalizationReasonDialogProps {
  showDialog: boolean;
  onClose: () => void;
  onConfirm: (reasonType: string, reasonDescription: string) => void;
  loading?: boolean;
}

// Opções pré-definidas de justificativas
const REASON_OPTIONS = [
  { value: "TOW_TRUCK_UNAVAILABLE", label: "Indisponibilidade de serviço de reboque" },
  { value: "TOW_TRUCK_DEALERSHIP", label: "Reboque acionado direto pela concessionária" },
  { value: "CUSTOMER_OWN_MEANS", label: "Cliente resolveu por meios próprios ou não deseja mais o serviço" },
  { value: "OTHER", label: "Outro (especificar)" },
];

export const FinalizeOccurrenceButton = (
  { occurrence, finishOccurrence, disabledAllFields, persona, stepId }:
    { occurrence?: Occurrence | null, finishOccurrence?: (occurrence: Occurrence, reasonType: string, reasonDescription: string, fromStepId?: number) => Promise<boolean>, disabledAllFields?: boolean, persona?: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP', stepId?: number }
) => {
  const [showFinalizationModal, setShowFinalizationModal] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  return (
    <>
      {persona === 'TOWER' ? 
        <button
        className={`btn btn-info ml-2 ${finishLoading ? "btn-loading disabled" : ""}`}
        type="button"
        onClick={() => setShowFinalizationModal(true)}
        disabled={finishLoading || disabledAllFields}
      >
        Finalizar
      </button> : null}

      <FinalizationReasonDialog
        showDialog={showFinalizationModal}
        onClose={() => setShowFinalizationModal(false)}
        onConfirm={async (reasonType, reasonDescription) => {
          setShowFinalizationModal(false);
          setFinishLoading(true);

          if (!occurrence) {
            setShowErrorDialog(true);
            return;
          }

          try {
            await finishOccurrence?.(occurrence, reasonType, reasonDescription, stepId) ?? false;
          } catch (error) {
            setShowErrorDialog(true);
            return;
          }

          setShowSuccessDialog(true);
          setFinishLoading(false);
        }}
        loading={finishLoading}
      />

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="Ocorrência finalizada com sucesso"
        body="A ocorrência foi finalizada com sucesso."
      />

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Erro ao finalizar ocorrência"
        body="Ocorreu um erro ao finalizar a ocorrência."
      />
    </>
  )
}

const FinalizationReasonDialog = ({
  showDialog,
  onClose,
  onConfirm,
  loading = false,
}: IFinalizationReasonDialogProps) => {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [customDescription, setCustomDescription] = useState<string>("");
  const [error, setError] = useState<string>("");

  const handleConfirm = () => {
    if (!selectedReason) {
      setError("Por favor, selecione uma justificativa para a finalização.");
      return;
    }

    if (selectedReason === "OTHER" && !customDescription.trim()) {
      setError("Por favor, descreva a justificativa personalizada.");
      return;
    }

    setError("");
    const finalDescription = selectedReason === "OTHER"
      ? customDescription.trim()
      : REASON_OPTIONS.find(option => option.value === selectedReason)?.label || "";

    onConfirm(selectedReason, finalDescription);
  };

  const handleClose = () => {
    setSelectedReason("");
    setCustomDescription("");
    setError("");
    onClose();
  };

  return (
    <Dialog
      show={showDialog}
      title="Justificativa para finalização antecipada"
      body={
        <div className="space-y-4">
          <div>
            <p className="text-gray-600 dark:text-neutral-400 mb-4">
              Para finalizar esta ocorrência de forma antecipada, é necessário informar a justificativa.
              Selecione uma das opções abaixo ou escolha "Outro" para especificar uma justificativa personalizada.
            </p>
          </div>

          <div className="space-y-3">
            <label className="block font-medium text-gray-700 dark:text-neutral-300">
              Justificativa para finalização antecipada *
            </label>

            <div className="space-y-2">
              {REASON_OPTIONS.map((option) => (
                <RadioButton
                  key={option.value}
                  name="finalizationReason"
                  checked={selectedReason === option.value}
                  onChange={() => setSelectedReason(option.value)}
                  className="mt-1"
                >
                  <span className="text-gray-700 dark:text-neutral-300">{option.label}</span>
                </RadioButton>
              ))}
            </div>
          </div>

          {selectedReason === "OTHER" && (
            <div className="space-y-2">
              <label className="block font-medium text-gray-700 dark:text-neutral-300">
                Descreva a justificativa para a finalização antecipada *
              </label>
              <textarea
                value={customDescription}
                onChange={(e) => setCustomDescription(e.target.value)}
                placeholder="Descreva a justificativa para a finalização desta ocorrência..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                maxLength={500}
              />
              <div className="text-gray-500 dark:text-neutral-400">
                {customDescription.length}/500 caracteres
              </div>
            </div>
          )}

          {error && (
            <div className="text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-3">
              {error}
            </div>
          )}
        </div>
      }
      footer={
        <div className="display-flex justify-content-end">
          <Button
            bsStyle="default"
            onClick={handleClose}
            className="margin-right-10"
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            bsStyle="primary"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? "Finalizando..." : "Finalizar Ocorrência"}
          </Button>
        </div>
      }
      bsSize={Dialog.SIZE_MD}
      onClose={handleClose}
    />
  );
};

export default FinalizationReasonDialog;
